import { ImageUpload } from "@/shared/components/images/image-upload";
import { useQpmMonitorShowImageQuery } from "@/services/backend/qpm/monitors/api/monitor-api";
import type { MonitorSnapDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import {
  useQpmCatalogItemDeleteImageMutation,
  useQpmCatalogItemSetImageMutation,
} from "@/services/backend/qpm/catalog/api/catalog-api";
import { Loader, Trash2 } from "lucide-react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Button } from "@/shared/components/ui/button";
import { usePersistenceStateUpdateOnTrigger } from "@/shared/lib/persistence-state/persistence-state-info";
import t from "@/lang/lang";
import { useCallback, useState } from "react";
import { v4 } from "uuid";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import type { SetItemImageReq } from "@/services/backend/qpm/catalog/types/catalog";

export function CatalogItemImageComponent({
  catalogItemId,
  image,
  className,
}: {
  catalogItemId: string;
  image?: MonitorSnapDTO["image"];
  className?: string;
}) {
  const imageFileId = image?.fileUuid ?? image?.fileId ?? "";
  const [request, setRequest] = useState<SetItemImageReq>({
    catalogItemId,
    fileId: "",
    file: null,
  });

  const {
    data: imageData,
    isLoading,
    error,
  } = useQpmMonitorShowImageQuery(
    {
      fileId: imageFileId,
      filePrefix: image?.prefix ?? "",
    },
    {
      skip: !image || !imageFileId || !image.prefix,
    },
  );
  const [
    upload,
    {
      error: uploadError,
      isLoading: isUploadLoading,
      isSuccess: isUploadSuccess,
      reset: resetUpload,
    },
  ] = useQpmCatalogItemSetImageMutation();
  const [
    deleteImage,
    {
      error: deleteError,
      isLoading: isDeleteLoading,
      isSuccess: isDeleteSuccess,
      reset: resetDelete,
    },
  ] = useQpmCatalogItemDeleteImageMutation();

  const { onTrigger: triggerDeleteImage } = usePersistenceStateUpdateOnTrigger(
    { catalogItemId },
    deleteImage,
    isDeleteLoading,
    deleteError,
    isDeleteSuccess,
    resetDelete,
    { toastError: true, toastSuccess: true },
  );

  const uploadFile = useCallback(
    (file: File) => {
      setRequest({
        catalogItemId,
        fileId: v4(),
        file,
      });
    },
    [catalogItemId],
  );

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    upload,
    isUploadLoading,
    uploadError,
    isUploadSuccess,
    resetUpload,
    250,
  );

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center">
        <Loader className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className={className ?? "flex w-full flex-col gap-1.5"}>
      <div className="rounded-sm border p-4">
        <ImageUpload
          id="catalog-item-picture"
          showImagePreview
          existingImageUrl={imageData && imageData.objectURL}
          onUpload={uploadFile}
          disabled={isUploadLoading || isDeleteLoading}
          err={parseRTKQueryError(uploadError ?? deleteError)}
          wrapperClassName="min-h-64"
          imageClassName="max-h-48 object-contain"
          actions={
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={triggerDeleteImage}
                    disabled={isUploadLoading || isDeleteLoading || !image}
                    className="cursor-pointer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Bild löschen")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
        />
      </div>
    </div>
  );
}
